// pages/case-tracking/index.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CaseCard from './CaseTrackingCards';
import { DCDashboardContainer } from '../../assets/theme/theme';
import { useCaseTrackingContext } from '../../contexts/CaseTrackingContext';
import { LoadingButton } from '@mui/lab';
import EmptyState from './EmptyState';

const CaseTracking: React.FC = () => {
  const { cases, createNewCase, getAllCases } = useCaseTrackingContext();
  const [loadingNewCase, setLoadingNewCase] = useState(false);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');

  const handleAddClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setReceiptNumber('');
  };

  const handleSubmit = async () => {
    setLoadingNewCase(true);
    if (receiptNumber.trim()) {
      await createNewCase(receiptNumber.trim());
      handleClose();
    }
    setLoadingNewCase(false);
  };

  const fetchAllCases = useCallback(async () => {
    setLoading(true);
    try {
      await getAllCases();
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  }, [getAllCases]);

  useEffect(() => {
    fetchAllCases();
  }, [fetchAllCases]);

  return (
    <DCDashboardContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>Case Tracking</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
          Add New Case
        </Button>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {cases.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <Grid container spacing={2}>
                {cases.map((caseItem) => (
                  <Grid
                    key={caseItem.receiptNumber}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                  >
                    <CaseCard data={caseItem} />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}
      {/* Add Case Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Add New Case</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Enter the receipt number for the new case:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            value={receiptNumber}
            onChange={(e) => setReceiptNumber(e.target.value.toUpperCase())}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            onClick={async () => {
              await handleSubmit();
            }}
            variant="contained"
            disabled={!receiptNumber.trim()}
            loading={loadingNewCase}
          >
            Add Case
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </DCDashboardContainer>
  );
};

export default CaseTracking;
